import {Async, AsyncPage, Empty} from 'Core/models/Async';
import {FilterOptionUnion, FilterOptionValue} from 'Core/models/FilterOption';
import {Page} from 'Core/models/Page';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {createAction} from 'Shared/utilities/actionCreator';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {LineOfBusiness} from 'Users/api';
import {Application} from 'Users/models/Application';
import {PreregisteredUser} from 'Users/models/PreregisteredUser';
import {UserFile, UserFileResults, ValidUserFile} from 'Users/models/UserFile';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';

export enum UserAdministrationActionType {
  CHANGE_USERS = 'userAdministration/CHANGE_USERS',
  FETCH_USERS = 'userAdministration/FETCH_USERS',
  CHANGE_USER_FILTER = 'userAdministration/CHANGE_USER_FILTER',
  LOAD_USER = 'userAdministration/LOAD_USER',
  DELETE_USER = 'userAdministration/DELETE_USER',
  RESET_USER_PASSWORD = 'userAdministration/RESET_USER_PASSWORD',
  CHANGE_FILE_UPLOAD = 'userAdministration/CHANGE_FILE_UPLOAD',
  BULK_UPLOAD_USER = 'userAdministration/BULK_UPLOAD_USER',
  GET_PREREGISTERED_USERS = 'userAdministration/GET_PREREGISTERED_USERS',
  CHANGE_PREREGISTERED_USERS = 'userAdministration/CHANGE_PREREGISTERED_USER',
  CHANGE_PREREGISTERED_USERS_FILTER = 'userAdministration/CHANGE_PREREGISTERED_USERS_FILTER',
  DELETE_PREREGISTRATION = 'userAdministration/DELETE_PREREGISTRATION',
}

export type UserType = {
  linesOfBusiness: LineOfBusiness[];
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  exclusivityStatus: {
    id: ExclusivityStatus;
  };
};

export type UserFilter = {
  linesOfBusiness: FilterOptionUnion;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  applicationId: string;
  exclusivityStatusId: string;
};

export const EmptyUserFilter: UserFilter = {
  linesOfBusiness: FilterOptionValue.EmptyOption(),
  email: '',
  username: '',
  firstName: '',
  lastName: '',
  applicationId: '',
  exclusivityStatusId: '',
};

export type PreregisteredUserFilter = {
  firstName: string;
  lastName: string;
  email: string;
  applicationId?: Application;
};

export const EmptyPreregisteredUserFilter: PreregisteredUserFilter = {
  firstName: '',
  lastName: '',
  email: '',
};

export const loadUser = (userId: string) => createAction(UserAdministrationActionType.LOAD_USER, {userId});
export const deleteUser = (userId: string, onComplete: () => void) =>
  createAction(UserAdministrationActionType.DELETE_USER, {userId, onComplete});
export const resetUserPassword = (userId: string, onComplete: () => void) =>
  createAction(UserAdministrationActionType.RESET_USER_PASSWORD, {userId, onComplete});
export const changeUserFilter = (filter: UserFilter) =>
  createAction(UserAdministrationActionType.CHANGE_USER_FILTER, filter);
export const changeUsers = (users: AsyncPage<Page<UserType>>) =>
  createAction(UserAdministrationActionType.CHANGE_USERS, users);
export const clearUserAdministrationFilter = () =>
  createAction(UserAdministrationActionType.CHANGE_USER_FILTER, {filter: DEFAULT_STATE.filter});
export const fetchUsers = (pageOption?: Page<UserType>, filter?: UserFilter) => {
  if (pageOption) {
    pageOption.links.forEach(x => {
      if (!x.href.includes('api/user')) {
        x.href = 'api/user' + x.href;
      }
    });
  }
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(
      () => PageFetchAction.Initial({filter}),
      page => PageFetchAction.Additional({existingPage: page, filter})
    )
  );

  return createAction(UserAdministrationActionType.FETCH_USERS, payload);
};
export const changeFileUpload = (file: UserFile) =>
  createAction(UserAdministrationActionType.CHANGE_FILE_UPLOAD, {file});
export const uploadUsers = (
  file: ValidUserFile,
  changeAsyncData: (registration: Async<UserFileResults>) => void,
  agencyFilters?: AgencyFilters
) =>
  createAction(UserAdministrationActionType.BULK_UPLOAD_USER, {
    changeAsyncData,
    file,
    agencyFilters,
  });

export const changePreregisteredUsers = (preregisteredUser: AsyncPage<Page<PreregisteredUser>>) =>
  createAction(UserAdministrationActionType.CHANGE_PREREGISTERED_USERS, preregisteredUser);
export const getPreregisteredUsers = (
  filter: PreregisteredUserFilter,
  page?: Page<PreregisteredUser>,
  encryptedIdentifier?: string
) => createAction(UserAdministrationActionType.GET_PREREGISTERED_USERS, {page, filter, encryptedIdentifier});
export const changePreregisteredUsersFilter = (preregisteredUserFilter: PreregisteredUserFilter) =>
  createAction(UserAdministrationActionType.CHANGE_PREREGISTERED_USERS_FILTER, preregisteredUserFilter);
export const deletePreregistration = (userId: string, filter: PreregisteredUserFilter, encryptedIdentifier?: string) =>
  createAction(UserAdministrationActionType.DELETE_PREREGISTRATION, {userId, encryptedIdentifier, filter});

type UserAction =
  | ReturnType<typeof changeUserFilter>
  | ReturnType<typeof fetchUsers>
  | ReturnType<typeof changeUsers>
  | ReturnType<typeof changeFileUpload>
  | ReturnType<typeof changePreregisteredUsers>
  | ReturnType<typeof changePreregisteredUsersFilter>;

type UserAdministrationState = {
  users: AsyncPage<Page<UserType>>;
  filter: UserFilter;
  preregisteredUsers: AsyncPage<Page<PreregisteredUser>>;
  preregisteredUserFilter: PreregisteredUserFilter;
};

export const DEFAULT_STATE: UserAdministrationState = {
  filter: EmptyUserFilter,
  users: new Empty(),
  preregisteredUsers: new Empty(),
  preregisteredUserFilter: EmptyPreregisteredUserFilter,
};

export default (state: UserAdministrationState = DEFAULT_STATE, action: UserAction) => {
  switch (action.type) {
    case UserAdministrationActionType.CHANGE_USER_FILTER:
      return {
        ...state,
        filter: action.payload,
      };
    case UserAdministrationActionType.CHANGE_USERS:
      return {
        ...state,
        users: action.payload,
      };
    case UserAdministrationActionType.CHANGE_FILE_UPLOAD:
      return {
        ...state,
        file: action.payload.file,
      };
    case UserAdministrationActionType.CHANGE_PREREGISTERED_USERS:
      return {
        ...state,
        preregisteredUsers: action.payload,
      };
    case UserAdministrationActionType.CHANGE_PREREGISTERED_USERS_FILTER:
      return {
        ...state,
        preregisteredUserFilter: action.payload,
      };
    default:
      return state;
  }
};
